import React, { useState, useEffect, useCallback } from 'react';
import axios, { API } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const ReportsTab = () => {
  const [reports, setReports] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedGoalId, setSelectedGoalId] = useState('');
  const [availableGoals, setAvailableGoals] = useState([]);
  const [goalInfo, setGoalInfo] = useState(null);

  const fetchReports = useCallback(async (goalId = '') => {
    setIsLoading(true);
    try {
      const url = goalId ? `${API}/reports?goal_id=${goalId}` : `${API}/reports`;
      const response = await axios.get(url);
      setReports(response.data.reports);
      setStats({
        total_reflection_days: response.data.total_reflection_days,
        total_extension_days: response.data.total_extension_days,
        total_reflections: response.data.total_reflections,
        total_extensions: response.data.total_extensions,
      });
      setGoalInfo(response.data.goal_info);
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchAvailableGoals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      setAvailableGoals(response.data);
    } catch (error) {
      console.error('Failed to load goals for filter:', error);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchAvailableGoals();
  }, [fetchReports, fetchAvailableGoals]);

  const handleGoalFilterChange = (goalId) => {
    setSelectedGoalId(goalId);
    fetchReports(goalId);
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-justb-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Loading reports...</p>
      </div>
    );
  }

  const emptyMessage = selectedGoalId
    ? 'Complete tasks with reflections or extend this goal to see reports here'
    : 'Complete tasks with reflections or extend goals to see reports here';

  return (
    <div className="space-y-6">
      {/* Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between bg-white p-4 rounded-lg border">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-900 mb-1">Filter by Goal:</label>
            <select
              value={selectedGoalId}
              onChange={(e) => handleGoalFilterChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-justb-primary focus:border-transparent"
            >
              <option value="">All Goals</option>
              {availableGoals.map((goal) => (
                <option key={goal.id} value={goal.id}>{goal.title}</option>
              ))}
            </select>
          </div>
          {selectedGoalId && goalInfo && (
            <div className="text-sm">
              <p className="font-medium text-gray-900">Selected Goal: {goalInfo.title}</p>
              <p className="text-gray-900">Status: {goalInfo.status}</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="bg-white border-2 border-justb-primary/20">
            <CardContent className="p-3 sm:p-4">
              <div className="text-center">
                <p className="text-justb-primary text-xs sm:text-sm font-medium">Reflection Days</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_reflection_days}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-2 border-justb-secondary/20">
            <CardContent className="p-3 sm:p-4">
              <div className="text-center">
                <p className="text-justb-secondary text-xs sm:text-sm font-medium">Extension Days</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_extension_days}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-2 border-slate-500/20">
            <CardContent className="p-3 sm:p-4">
              <div className="text-center">
                <p className="text-slate-600 text-xs sm:text-sm font-medium">Total Reflections</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_reflections}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-2 border-amber-500/20">
            <CardContent className="p-3 sm:p-4">
              <div className="text-center">
                <p className="text-amber-600 text-xs sm:text-sm font-medium">Total Extensions</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.total_extensions}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Reports */}
      <div className="space-y-4">
        {reports && reports.length > 0 ? (
          reports.map((dayReport) => (
            <Card key={dayReport.date} className="border-l-4 border-l-justb-secondary">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900 flex items-center">
                  <Calendar className="mr-2 h-5 w-5 text-justb-secondary" />
                  {new Date(dayReport.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {dayReport.reflections?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-justb-primary mb-3 flex items-center">
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Task Reflections ({dayReport.reflections.length})
                    </h4>
                    <div className="space-y-3">
                      {dayReport.reflections.map((r) => (
                        <div key={r.task_id} className="bg-green-50 p-4 rounded-lg border border-green-200">
                          <div className="mb-2">
                            <p className="font-medium text-gray-900">{r.goal_title}</p>
                            <p className="text-sm text-gray-600">{r.task_description}</p>
                          </div>
                          <div className="bg-white p-3 rounded border">
                            <p className="text-sm text-gray-800 italic">"{r.reflection}"</p>
                          </div>
                          {r.time_spent && (
                            <div className="mt-2">
                              <Badge variant="outline" className="text-justb-secondary">{r.time_spent} minutes</Badge>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dayReport.extensions?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-amber-600 mb-3 flex items-center">
                      <Clock className="mr-2 h-4 w-4" />
                      Goal Extensions ({dayReport.extensions.length})
                    </h4>
                    <div className="space-y-3">
                      {dayReport.extensions.map((ext) => (
                        <div key={ext.extension_id} className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                          <div className="mb-2">
                            <p className="font-medium text-gray-900">{ext.goal_title}</p>
                            <p className="text-sm text-amber-700">
                              Extended by {ext.days_extended} day(s) - {ext.extension_type}
                            </p>
                          </div>
                          <div className="bg-white p-3 rounded border">
                            <p className="text-sm text-gray-800 italic">"{ext.reason}"</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!dayReport.reflections?.length) && (!dayReport.extensions?.length) && (
                  <div className="text-center py-4 text-gray-500">
                    <p className="text-sm">No reflections or extensions recorded for this day</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2 text-gray-900">No Reports Yet</h3>
              <p className="text-gray-600 mb-6">{emptyMessage}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReportsTab;
